import React, { useState, useEffect, ChangeEvent } from 'react';
import { Doctor } from '../../../services/DoctorService';

import { DoctorService } from '../../../services/DoctorService';
import { AuthService } from '../../../services/AuthService';
import { CreateDoctorPayload } from 'services/DoctorService';
import './AddDoctor.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { SelectChangeEvent } from '@mui/material';

interface AddDoctorProps {
  open?: boolean;
  onClose?: () => void;
  onDoctorAdded?: (doctor: Doctor) => void;
  doctor?: Doctor;
  isEditing?: boolean;
}

interface FormData {
  nom: string;
  prenom: string;
  email: string;
  mot_de_passe: string;
  pays: string;
  role?: string;
}

const AddDoctor: React.FC<AddDoctorProps> = ({ open = true, onClose = () => {}, onDoctorAdded, doctor }) => {
  const [formData, setFormData] = useState<FormData>({
    nom: '',
    prenom: '',
    email: '',
    mot_de_passe: 'Tachafy@25',
    pays: 'Tunisia',
    role: 'doctorTester',
  });

  useEffect(() => {
    if (doctor) {
      setFormData({
        nom: doctor.nom || '',
        prenom: doctor.prenom || '',
        email: doctor.email || '',
        mot_de_passe: doctor.mot_de_passe || 'Tachafy@25',
        pays: doctor.pays || 'Tunisia',
      });
    } else {
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        mot_de_passe: 'Tachafy@25',
        pays: 'Tunisia',
      });
    }
  }, [doctor]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
  setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
};

  const validateEmail = (email: string): string | null => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) return 'Email is required';
    if (!emailRegex.test(email)) return 'Invalid email format (e.g., user@domain.com)';
    return null;
  };

  const validateForm = (): string | null => {
    const emailError = validateEmail(formData.email);
    if (emailError) return emailError;
    if (!formData.nom.trim()) return 'Nom is required';
    if (!formData.prenom.trim()) return 'Prénom is required';
    if (!formData.mot_de_passe.trim()) return 'Password is required';
    if (!formData.pays || formData.pays === 'Select') return 'Please select a country';
    return null;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const payload: CreateDoctorPayload = {
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        email: formData.email.trim(),
        mot_de_passe: formData.mot_de_passe.trim(),
        pays: formData.pays,
        role: 'DoctorTester',
      };

      if (doctor) {
        setIsEditing(true);
        await AuthService.updateUser(doctor.id, payload);
      } else {
        setIsEditing(false);
        await DoctorService.createDoctor(payload);
        setShowSuccessDialog(true);
      }

      onDoctorAdded?.({ ...payload, id: doctor?.id || Date.now() } as Doctor);
    } catch (err: any) {
      const errorMessage = err || 'Failed to add/update doctor';
      setError(errorMessage);
      try {
        const userId = err.response?.data?.user_id || err.response?.data?.id;
        if (userId && !doctor) {
          await AuthService.deleteUser(userId);
        }
      } catch (deleteErr) {
        console.error('Error deleting user after doctor creation failure:', deleteErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    onClose();
    setFormData({ nom: '', prenom: '', email: '', mot_de_passe: 'Tachafy@25', pays: 'Tunisia' });
  };

  return (
     <>
      <Dialog
        open={open && !showSuccessDialog}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            margin: { xs: '8px', sm: '16px', md: '24px' },
            width: '100%',
            maxWidth: '1024px',
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 700,
          fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
          color: '#1f2937',
          padding: { xs: '12px 16px', sm: '16px 20px', md: '24px 32px' },
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {doctor ? 'Edit Doctor' : 'Add New Doctor'}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', p:1 }}>
            {/* Doctor Information Section */}
            <Box>
              <Typography variant="h4" sx={{
                fontFamily: 'Manrope, sans-serif',
                margin: '0 0 16px 0',
                color: '#374151',
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>
                Doctor Informations
              </Typography>
              
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: 'repeat(2, 1fr)', 
                  lg: 'repeat(3, 1fr)' 
                },
                gap: '16px'
              }}>
                <TextField
                  name="nom"
                  label="Nom"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Enter nom"
                  fullWidth
                  size="small"
                />
                <TextField
                  name="prenom"
                  label="Prénom"
                  value={formData.prenom}
                  onChange={handleChange}
                  placeholder="Enter prénom"
                  fullWidth
                  size="small"
                />
                <TextField
                  name="email"
                  label="Adresse E-mail"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  fullWidth
                  size="small"
                  error={error?.includes('email')}
                  sx={{ 
                    gridColumn: { 
                      xs: '1 / -1', 
                      sm: '1 / -1', 
                      lg: 'auto' 
                    } 
                  }}
                />
              </Box>
            </Box>

            {/* Doctor Address Section */}
            <Box>
              <Typography variant="h5" sx={{
                margin: '0 0 16px 0',
                color: '#374151',
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>
                Doctor Address
              </Typography>
              
              <Box sx={{ width: '100%' }}>
                <Select
                  name="pays"
                  value={formData.pays}
                  onChange={handleSelectChange}
                  fullWidth
                  size="small"
                  displayEmpty
                  renderValue={value => value || "Select Country"}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="France">France</MenuItem>
                  <MenuItem value="Tunisia">Tunisia</MenuItem>
                  
                </Select>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          padding: { xs: '12px', sm: '16px', md: '24px' },
          justifyContent: 'flex-end',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <Button
            onClick={onClose}
            style={{
                  padding: '12px 24px',
                  border: '1px solid #7287FC',
                  borderRadius: '8px',
                  background: '#fff',
                  color: '#7287FC',
                  fontWeight: '500',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '120px',
                }}
            onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'rgba(114, 135, 252, 0.05)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#fff';
                }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  backgroundColor: loading ? '#a3bffa' : '#7287FC',
                  color: '#fff',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '120px',
                }}
                onMouseEnter={(e) => {
                  if (!loading) (e.target as HTMLButtonElement).style.backgroundColor = '#5c6bc0';
                }}
                onMouseLeave={(e) => {
                  if (!loading) (e.target as HTMLButtonElement).style.backgroundColor = '#7287FC';
                }}
            
          >
            {doctor ? 'Update' : 'Active Account'}
          </Button>
        </DialogActions>

        {error && (
          <Typography color="error" sx={{ 
            textAlign: 'center',
            padding: '0 24px 16px',
            fontSize: '0.75rem',
            fontWeight: 500
          }}>
            {error}
          </Typography>
        )}
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessClose}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '20px',
            maxWidth: { xs: '90vw', sm: '500px' },
            width: '100%',
            margin: { xs: '8px', sm: '16px' }
          }
        }}
      >
        <DialogContent sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: { xs: '16px', sm: '24px 32px' }
        }}>
          <CheckCircleIcon sx={{
            fontSize: { xs: '40px', sm: '48px' },
            color: '#22c55e',
            marginBottom: '16px'
          }} />
          <Typography variant="h6" sx={{
            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
            fontWeight: 700,
            color: '#1d1d4e',
            marginBottom: '8px'
          }}>
            {doctor ? 'Doctor Updated Successfully' : 'Doctor Added Successfully'}
          </Typography>
          <Typography variant="body1" sx={{
            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
            color: '#7a7e9f',
            marginBottom: '16px'
          }}>
            {doctor ? 'The doctor\'s information has been updated.' : 'We sent to our new doctor an e-mail to confirm account activation.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'center',
          paddingBottom: { xs: '16px', sm: '24px' },
          paddingLeft: { xs: '16px', sm: '24px' },
          paddingRight: { xs: '16px', sm: '24px' }
        }}>
          <Button
            onClick={handleSuccessClose}
            variant="contained"
            sx={{
              minWidth: { xs: '120px', sm: '160px' },
              height: { xs: '40px', sm: '48px' },
              borderRadius: '12px',
              backgroundColor: '#7287fc',
              color: '#fff',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#5a6be0'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddDoctor;
